/**
 * Deep-link prefill for the API-type tool pages (FRIDGE-364/365).
 *
 * The embeddable widgets under `public/widgets/*.js` are the site's link
 * magnet: a publisher drops the script on their page, a visitor types a ZIP or
 * a model number into it, and the call-to-action carries that value to the full
 * tool as a query parameter. The three widgets that do this are
 *
 *   refrigerator-rebate-finder        → /tools/refrigerator-rebate-finder?zip=
 *   refrigerator-recall-checker       → /tools/refrigerator-recall-checker?q=
 *   refrigerator-water-filter-finder  → /tools/refrigerator-water-filter-finder?q=
 *
 * ...and until FRIDGE-364 none of the three tool pages read the parameter, so
 * the value the visitor typed was silently dropped and they had to type it
 * again on arrival. This module is the one place that parsing lives.
 *
 * It reads `window.location.search` rather than Next's `useSearchParams()`
 * deliberately: these tool pages are statically generated, and
 * `useSearchParams()` in a statically-rendered client component requires a
 * Suspense boundary and opts the route into dynamic rendering. Reading the
 * location inside a mount effect keeps the pages static and cannot change how
 * they build.
 *
 * Pure string in, pure string out — no React, no Next, no DOM — so it is unit
 * testable in the repository's node-environment jest.
 */

/** Longest value we will accept from a URL, to keep a hostile link bounded. */
const MAX_VALUE_LENGTH = 120

/**
 * Pull one query parameter out of a `location.search` string and normalise it
 * for use as a tool's search term.
 *
 * Returns `''` for anything unusable — missing parameter, empty value,
 * whitespace only, or a malformed query string — so callers can treat the empty
 * string as "no deep link, leave the tool in its pristine state".
 *
 * @param search Raw `location.search`, with or without the leading `?`.
 * @param key    Parameter name, e.g. `q` or `zip`.
 */
export function readDeepLinkParam(search: string, key: string): string {
  if (!search || !key) return ''
  let params: URLSearchParams
  try {
    params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search)
  } catch {
    return ''
  }
  const raw = params.get(key)
  if (!raw) return ''
  // Collapse internal whitespace as well as trimming: widget inputs are typed
  // by hand and "WRX735  SDHZ" should not become a different query from
  // "WRX735 SDHZ".
  const value = raw.replace(/\s+/g, ' ').trim()
  if (!value) return ''
  return value.slice(0, MAX_VALUE_LENGTH)
}

/**
 * ZIP variant: the rebate finder only ever searches on a US 5-digit ZIP, and
 * its own input is `maxLength=5`, so a deep link carrying anything else is
 * discarded rather than run as a doomed query.
 */
export function readDeepLinkZip(search: string): string {
  const value = readDeepLinkParam(search, 'zip')
  return /^\d{5}$/.test(value) ? value : ''
}
